// Pure resolution logic for the floating "Add thesis" prompt bar (MOR-649).
// Kept out of the component file so the bar's only export is the component
// itself (Fast Refresh / react-doctor only-export-components).

import type { GetTradesRow } from '@morfi/backend-sdk-v2/client';

/** Row shape returned by `useGetTrades` under `TRADES_PROJECTION`. */
export interface TradeRow {
  signature: string;
  outcomeIndex: number;
  type: GetTradesRow['type'];
  usdcAmount: string;
  trader?: { walletAddress?: string };
}

/** The viewer's newest buy/sell, resolved into everything the bar + dialog need. */
export interface PromptTrade {
  signature: string;
  walletAddress: string;
  outcomeIndex: number;
  direction: 'buy' | 'sell';
  amountD6: string;
  outcomeLabel: string | null;
}

export interface OutcomeLike {
  outcomeIndex: number | string;
  label?: string;
}

/**
 * Whether a trades-row signature is the real on-chain tx signature (which
 * `add_thesis` keys on) rather than a realtime placeholder.
 *
 * `useGetTrades` realtime-prepends every trade as it lands, but the v0 trade
 * event carries no tx signature — `buildTradeRowFromEvent` synthesises one from
 * the 15-byte `sequenceId` (30 hex chars) or `<actor>:<ts>`. Submitting a
 * thesis against that placeholder orphans it because it cannot join to the
 * debate feed. A real Solana signature is base58, ~88 chars, and never contains
 * ':'. The REST snapshot replaces the placeholder within the trades staleTime,
 * so the trade becomes thesis-able a beat after it lands.
 */
function isRealTradeSignature(signature: string): boolean {
  return signature.length >= 64 && !signature.includes(':');
}

/**
 * The viewer's newest buy/sell on this market, but only when it has no thesis
 * yet according to the debate feed or session-local overlay. Returns `null` to
 * hide the bar — newest trade already explained, or no buy/sell to explain.
 *
 * The ownership filter is load-bearing: `useGetTrades` subscribes to
 * `morfi.market.${pubkey}.user.*.trade.v0` (EVERY trader) and its `prepend`
 * recipe pushes every market trade onto our `user`-scoped cache too. Without
 * the `walletAddress === userPubkey` guard a stranger's trade lands at
 * `rows[0]`, flips the resolved trade on every market trade (the flicker) and
 * mislabels it as "You bought".
 *
 * Hide is permanent for a given trade. A trade counts as thesised when its
 * signature appears in `thesisSignatures` — the signatures
 * from the market's debate feed (`useGetTheses`), the real source of truth that
 * survives reload — or in the session-local overlay `localTheses` (instant hide
 * right after submit, before the feed realtime lands).
 */
export function resolvePromptTrade(
  rows: ReadonlyArray<TradeRow>,
  localTheses: ReadonlyMap<string, string>,
  thesisSignatures: ReadonlySet<string>,
  outcomes: ReadonlyArray<OutcomeLike>,
  userPubkey: string,
): PromptTrade | null {
  const latest = rows.find(
    (r) =>
      (r.type === 'buy' || r.type === 'sell') &&
      r.trader?.walletAddress === userPubkey &&
      isRealTradeSignature(r.signature),
  );
  if (latest === undefined) return null;

  const hasThesis = localTheses.has(latest.signature) || thesisSignatures.has(latest.signature);
  if (hasThesis) return null;

  const outcomeIndex = Number(latest.outcomeIndex);
  const match = outcomes.find((o) => Number(o.outcomeIndex) === outcomeIndex);
  const label = match?.label;
  return {
    signature: latest.signature,
    walletAddress: latest.trader?.walletAddress ?? userPubkey,
    outcomeIndex,
    direction: latest.type === 'sell' ? 'sell' : 'buy',
    amountD6: String(latest.usdcAmount ?? '0'),
    outcomeLabel: label !== undefined && label.length > 0 ? label : null,
  };
}
